import { create } from 'zustand';

export interface OrgMember {
  userId: string;
  displayName: string;
  email: string | null;
  role: string;
  joinedAt: string;
}

export interface OrgInvite {
  id: string;
  email: string;
  role: string;
  invitedBy: string;
  createdAt: string;
}

interface OrgMemberState {
  members: OrgMember[];
  invites: OrgInvite[];
  currentUser: OrgMember | null;
  loading: boolean;
  error: string | null;
  clearCurrentUser: () => void;
  loadMembers: (token?: string | null) => Promise<void>;
  loadCurrentUser: (token?: string | null) => Promise<void>;
  changeRole: (userId: string, role: string, token?: string | null) => Promise<void>;
  removeMember: (userId: string, token?: string | null) => Promise<void>;
  loadInvites: (token?: string | null) => Promise<void>;
  createInvite: (email: string, role: string, token?: string | null) => Promise<void>;
  revokeInvite: (inviteId: string, token?: string | null) => Promise<void>;
}

export const useOrgMemberStore = create<OrgMemberState>()((set, get) => ({
  members: [],
  invites: [],
  currentUser: null,
  loading: false,
  error: null,

  clearCurrentUser: () => {
    set({ currentUser: null, error: null });
  },

  loadMembers: async (token) => {
    if (get().loading) return;
    set({ loading: true, error: null });
    try {
      const headers: HeadersInit = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch('/api/dotnet/members', { headers });
      if (!response.ok) throw new Error('Failed to load members.');

      const members = (await response.json()) as OrgMember[];
      set({ members, loading: false });
    } catch (err) {
      set({
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load members.',
      });
    }
  },

  loadCurrentUser: async (token) => {
    try {
      const headers: HeadersInit = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch('/api/dotnet/members/me', { headers });
      if (!response.ok) throw new Error('Failed to load current user.');

      const currentUser = (await response.json()) as OrgMember;
      set({ currentUser });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to load current user.',
      });
    }
  },

  changeRole: async (userId, role, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`/api/dotnet/members/${userId}/role`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ role }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error ?? 'Failed to change role.');
    }

    const updated = (await response.json()) as OrgMember;
    set({
      members: get().members.map((m) => (m.userId === userId ? updated : m)),
      error: null,
    });
  },

  removeMember: async (userId, token) => {
    const headers: HeadersInit = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`/api/dotnet/members/${userId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error ?? 'Failed to remove member.');
    }

    set({
      members: get().members.filter((m) => m.userId !== userId),
      error: null,
    });
  },

  loadInvites: async (token) => {
    try {
      const headers: HeadersInit = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch('/api/dotnet/members/invites', { headers });
      if (!response.ok) return; // non-admins get 403 — silently skip

      const invites = (await response.json()) as OrgInvite[];
      set({ invites });
    } catch {
      // ignore — non-admin users can't list invites
    }
  },

  createInvite: async (email, role, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch('/api/dotnet/members/invites', {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, role }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error ?? 'Einladung konnte nicht erstellt werden.');
    }

    const invite = (await response.json()) as OrgInvite;
    set({
      invites: [invite, ...get().invites],
      error: null,
    });
  },

  revokeInvite: async (inviteId, token) => {
    const headers: HeadersInit = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`/api/dotnet/members/invites/${inviteId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error ?? 'Einladung konnte nicht zurückgezogen werden.');
    }

    set({
      invites: get().invites.filter((i) => i.id !== inviteId),
      error: null,
    });
  },
}));
